import { VpChevronLeftIcon } from '@vtmn-play/icons/react';
import { VpDivider, VpIconButton, VpLoader } from '@vtmn-play/react';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormCreateUserNeed } from '../../hooks/useFormCreateUserNeed';
import { Footer } from '../footer';
import { UserNeedBenefits } from './formParts/UserNeedBenefits';
import { UserNeedDetails } from './formParts/UserNeedDetails';
import { UserNeedSegmentations } from './formParts/UserNeedSegmentation';

import { SpoCreateUserNeedBodyUserNeeds } from '@spo/types';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../contexts/ToastContext';
import { useCreateUserNeed } from '../../hooks/mutations/useCreateUserNeed';
import { CancelModal } from '../architecture/CancelModal';
import './create-un-form.scss';

export type UserNeedBenefit = {
  id: number;
  label: string;
  notation: string;
};

export type TextField = {
  text: string;
  hasError: boolean;
};

export type UserNeedFormData = {
  label: TextField;
  description: TextField;
  usageSegmentationType: number;
  usageSegmentationValue: number;
  benefits: UserNeedBenefit[];
};

export const initialFormData: UserNeedFormData = {
  label: {
    text: '',
    hasError: false,
  },
  description: {
    text: '',
    hasError: false,
  },
  usageSegmentationType: 0,
  usageSegmentationValue: 0,
  benefits: [
    {
      id: 0,
      label: '',
      notation: '',
    },
  ],
};

export interface CreateUserNeedFormProps {
  setOpenDrawerAddUserNeeds: (value: boolean) => void;
  openExitSettingsModal: boolean;
  setOpenExitSettingsModal: (value: boolean) => void;
  setFormIsEmpty: (value: boolean) => void;
}

export const CreateUserNeedForm = ({
  setOpenDrawerAddUserNeeds,
  openExitSettingsModal,
  setOpenExitSettingsModal,
  setFormIsEmpty,
}: CreateUserNeedFormProps) => {
  const [userNeedData, setUserNeedData] =
    useState<UserNeedFormData>(initialFormData);

  const { t } = useTranslation();

  const params = new URLSearchParams(document.location.search);
  const sportId = params.get('sportId') || '';
  const natureId = params.get('natureId') || '';
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const { data: formCreateUserNeedData, isFetching } = useFormCreateUserNeed({
    natureId,
    sportId: Number(sportId),
    language: 'EN',
  });

  const exitUserNeedCreation = () => {
    // Remove body overflow hidden to enable scroll on main page, due to new modal
    document.body.style.overflow = '';
    setOpenDrawerAddUserNeeds(false);
    setOpenExitSettingsModal(false);
    setUserNeedData(initialFormData);
    setFormIsEmpty(true);
  };

  const mutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ['useProductNatureData', natureId, Number(sportId)],
    });
    exitUserNeedCreation();
  };

  const mutationError = () => {
    addToast(
      t('architecture.drawer.user-need.error'),
      'danger',
      4500,
      'drawer-user-need_toastr_error',
    );
  };

  const { mutate, isPending: isCreatingUserNeeds } = useCreateUserNeed(
    sportId,
    natureId,
    mutationSuccess,
    mutationError,
  );

  const createUserNeed = () => {
    const userNeedToCreate: SpoCreateUserNeedBodyUserNeeds = {
      sportId: Number(sportId),
      productNatureId: Number(natureId),
      label: {
        fr: userNeedData.label.text,
        en: userNeedData.label.text,
      },
      description:
        userNeedData.description.text.length > 0
          ? {
              fr: userNeedData.description.text,
              en: userNeedData.description.text,
            }
          : null,
      segmentation: userNeedData.usageSegmentationType
        ? {
            type: userNeedData.usageSegmentationType,
            value: userNeedData.usageSegmentationValue,
          }
        : null,
      productBenefits: userNeedData.benefits.map((benefit) => ({
        id: benefit.id,
        notation: benefit.notation,
      })),
    };

    mutate(userNeedToCreate);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    createUserNeed();
  };

  const updateUN = (
    field: keyof UserNeedFormData,
    newValue: string | number | UserNeedBenefit[] | TextField,
  ) => {
    setUserNeedData({ ...userNeedData, [field]: newValue });
  };

  const isValidateActionDisabled = () =>
    !userNeedData.label.text ||
    userNeedData.label.hasError ||
    userNeedData.description.hasError ||
    (userNeedData.usageSegmentationType !== 0 &&
      userNeedData.usageSegmentationValue === 0) ||
    !userNeedData.benefits[0].label ||
    isCreatingUserNeeds;

  const formIsEmpty = () =>
    !userNeedData.label.text &&
    !userNeedData.description.text &&
    !userNeedData.usageSegmentationType &&
    !userNeedData.usageSegmentationValue &&
    userNeedData.benefits.length > 0;

  useEffect(() => {
    setFormIsEmpty(formIsEmpty());
  }, [formIsEmpty]);

  return (
    <>
      <form
        className="drawer-nature-content"
        data-testid="create-user-need-form"
        onSubmit={onSubmit}
      >
        <div className="user-need-form-content">
          <div className="drawer-header">
            <VpIconButton
              onClick={() => {
                formIsEmpty()
                  ? exitUserNeedCreation()
                  : setOpenExitSettingsModal(true);
              }}
              variant="secondary"
              size="small"
              aria-label="Go back on previous page"
              data-testid="create-un-form-go-back_link"
            >
              <VpChevronLeftIcon />
            </VpIconButton>
            <div className="drawer-title">
              {t('architecture.drawer.user-need.create')}
            </div>
          </div>
          {formCreateUserNeedData ? (
            <>
              <UserNeedDetails
                setUnLabel={(value) => updateUN('label', value)}
                setUnDescr={(value) => updateUN('description', value)}
              />
              <UserNeedSegmentations
                formCreateUserNeedData={formCreateUserNeedData}
                setUnSegmentationType={(id) =>
                  updateUN('usageSegmentationType', id)
                }
                setUnSegmentationValue={(id) =>
                  updateUN('usageSegmentationValue', id)
                }
              />
              <VpDivider className="user-need-divider" />
              <UserNeedBenefits
                availableProductBenefits={
                  formCreateUserNeedData?.productBenefits || []
                }
                setUnBenefits={(value) => updateUN('benefits', value)}
              />
            </>
          ) : (
            <VpLoader data-testid="vp_loader" />
          )}
        </div>
        <Footer
          ctaLabel={t('footer.save-and-quit-button')}
          validateAction={createUserNeed}
          cancelAction={() =>
            formIsEmpty()
              ? exitUserNeedCreation()
              : setOpenExitSettingsModal(true)
          }
          isValidateActionDisabled={isValidateActionDisabled()}
          isLoading={isFetching}
        />
      </form>
      <CancelModal
        isOpenModal={openExitSettingsModal}
        onClose={() => setOpenExitSettingsModal(false)}
        onClick={exitUserNeedCreation}
      />
    </>
  );
};
